import React, { ReactElement } from 'react';
import { Document, Page, StyleSheet, Text, Font } from '@react-pdf/renderer';
import { Entente } from '../../../../../../common/src/entities';
import { DateTime } from 'luxon';
import inter from '@fontsource/inter/files/inter-latin-400-normal.woff';
import inter500 from '@fontsource/inter/files/inter-latin-500-normal.woff';
import interBold from '@fontsource/inter/files/inter-latin-700-normal.woff';
import CommonHeaderPDF, { beautifyType } from './CommonHeaderPDF';
import SoumissionContent from './Components/SoumissionContent';
import ReservationContent from './Components/ReservationContent';
import FactureContent from './Components/FactureContent';
import BonCommandeContent from './Components/BonCommandeContent';
import EtatCompteContent from './Components/EtatCompteContent';

Font.register({
    family: 'Inter',
    fonts: [{ src: inter }, { src: inter500, fontWeight: 500 }, { src: interBold, fontWeight: 700 }],
});

export default function EntentePDF({ entente, type }: { entente: Entente; type: string }): ReactElement {
    const styles = StyleSheet.create({
        body: {
            fontFamily: 'Inter',
            padding: '2cm',
            fontSize: 12,
            textAlign: 'justify',
        },
        page: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#333',
            backgroundColor: '#FFF',
            top: -25,
            marginBottom: 5,
        },
        logo: { top: -15 },

        coverTitle: {
            fontSize: 32,
            fontWeight: 'bold',
        },
        h1: {
            width: '100%',
            fontSize: 24,
            textAlign: 'center',
        },
        imageContainer: {
            width: '100%',
            height: 525,
            overflow: 'hidden',
        },
        image: {
            width: '100%',
            height: '100%',
            objectFit: 'contain',
        },
        fixed: {
            position: 'absolute',
            left: 0,
            right: 0,
            textAlign: 'center',
            color: 'grey',
        },
        header: {
            top: 13,
        },
        header2: {
            top: 16,
            fontSize: 10,
        },
        footer: {
            fontSize: 10,
            position: 'absolute',
            bottom: 20,
            right: 20,
        },
        watermark: {
            position: 'absolute',
            alignSelf: 'center',
            transform: 'rotate(45deg)',
            fontSize: 45,
            top: '50%',
            color: '#f4f4f4',
        },
        pillsContainer: {
            flexDirection: 'row',
            flexWrap: 'wrap', // This will wrap the pills to the next line if they exceed the width of the parent container
            alignItems: 'center',
        },
        pill: {
            backgroundColor: '#ffffff',
            paddingVertical: 5,
            paddingHorizontal: 10,
            borderRadius: 20,
            fontSize: 8,
            borderColor: '#0088ff',
            borderWidth: 1,
            marginRight: 5,
            marginBottom: 5,
            marginTop: 5,
        },
        disclaimer: {
            flexDirection: 'row',
            justifyContent: 'flex-start',
            alignItems: 'center',
        },
        boldText: {
            fontSize: 12,
            fontWeight: 'bold',
            marginRight: 5,
        },
        normalText: {
            fontSize: 10,
        },
    });

    function TronquerAvecEllipsis(texte, longueurMax) {
        return texte.length > longueurMax ? texte.slice(0, longueurMax - 3) + '...' : texte;
    }

    return (
        <Document>
            <Page style={styles.body}>
                <Text style={[styles.header, styles.fixed]} fixed>
                    Entente - {TronquerAvecEllipsis(entente.name, 80)} - {beautifyType(type)}
                </Text>
                <Text style={[styles.header2, styles.fixed]} fixed>
                    {'\n'} PDF créé :{' '}
                    {DateTime.local()
                        .setLocale('fr')
                        .toLocaleString({ month: 'numeric', day: 'numeric', year: 'numeric' })}
                    . Document à l&apos;usage exclusif des clients de Solution Animation.
                </Text>

                <Text style={[styles.footer, styles.fixed]} fixed>
                    Pour toutes questions : Isaac Barbe 514-942-5155 isaacbarbe@solutionanimation.com
                </Text>
                <Text
                    style={styles.footer}
                    render={({ pageNumber, totalPages }) => `Page ${pageNumber} de ${totalPages}`}
                    fixed
                />

                <CommonHeaderPDF entente={entente} type={type} />

                {type === 'soumission' && <SoumissionContent entente={entente} />}
                {type === 'reservation' && <ReservationContent entente={entente} />}
                {type === 'facture' && <FactureContent entente={entente} />}
                {type === 'bonDeCommande' && <BonCommandeContent entente={entente} />}
                {type === 'etatDeCompte' && <EtatCompteContent entente={entente} />}
            </Page>
        </Document>
    );
}
